import React, { forwardRef, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import shortid from 'shortid'

import { store } from 'redux/store'
import { useActionless, useAppSelector } from 'redux/hooks'
import { useStopBodyScroll } from 'hooks/useScrollEffects'
import { useSwipeEffects } from 'hooks/useSwipeEffects'
import { useTransition } from 'hooks/useTransitions'
import useKeylistener from 'hooks/useKeylistener'

import { SET_ACTIVE_STATE } from 'redux/active/actions/types'
import { Option } from 'components/elements/FloatingToolbar/types'

import Icon from 'components/elements/Icon'
import FloatingToolbar from 'components/elements/FloatingToolbar'

import styles from './globalOverlayStack.module.scss'

type Props = {
  open: boolean
  onClose: () => void
  onOpen?: () => void
  children: React.ReactNode
  className?: string
  mobileToolbarOptions?: Option[]
  rootClassName?: string
  backButtonText?: string
  remainMounted?: boolean
  id?: string
  noWeirdClose?: boolean
  disableSwipeToClose?: boolean
}

const GlobalOverlayStack = forwardRef(
  (
    {
      open,
      onClose,
      onOpen,
      children,
      className = '',
      mobileToolbarOptions = [],
      rootClassName = '',
      remainMounted,
      noWeirdClose = false,
      disableSwipeToClose = false,
      id: parentId,
    }: Props,
    parentRef: any,
  ) => {
    if (typeof document === 'undefined') return null

    const id = useRef(parentId || shortid.generate())
    const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null)

    const setNoScroll = useStopBodyScroll(open)

    const stackOverlayIds = useAppSelector(state => state.active.stackOverlayIds)
    const hideStackOverlay = useAppSelector(state => state.active.hideStackOverlay)

    const [setActiveState] = useActionless(SET_ACTIVE_STATE)

    const [closeClickStarted, setCloseClickStarted] = useState(false)
    const [skipTransition, setSkipTransition] = useState(false)

    // matain isRootComponent if you're the last overlay shown. Doing so allows the close animation to finish
    const isRootComponent = !stackOverlayIds.length || stackOverlayIds[0] === id.current
    const isActiveElement = stackOverlayIds[stackOverlayIds.length - 1] === id.current

    const [contentRef, contentState] = useTransition(open && isActiveElement)
    const [containerTransitionRef, containerState] = useTransition(open, {
      skipTransition: skipTransition || hideStackOverlay,
      enteringClassName: styles.overlayEntering,
      exitingClassName: styles.overlayExiting,
    })

    useKeylistener(
      e => {
        if (!onClose || !open) return

        if (e.key === 'Escape') {
          e.preventDefault() // prevents exiting fullscreen on macos (I think)
          onClose()
        }
      },
      [open],
    )

    useEffect(() => {
      if (open) {
        setNoScroll(true)

        if (onOpen) onOpen()

        if (!stackOverlayIds.includes(id.current)) setActiveState({ stackOverlayIds: [...stackOverlayIds, id.current] })

        return
      }

      setSkipTransition(false)
      setNoScroll(false)

      const isInStack = stackOverlayIds.includes(id.current)

      if (!isInStack) return // Don't update redux unless we have to

      setActiveState({ stackOverlayIds: stackOverlayIds.filter(overlayId => overlayId !== id.current) })
    }, [open])

    // Cleanup
    useEffect(() => {
      // Regardless of the components state when this component is unmounted
      // Check the store, and remove the ID from the stack if it still exists
      // This is a failsafe to be sure the ID is removed from the stack even if the element is removed via unorthodox means
      return () => {
        const currentIds = store.getState().active.stackOverlayIds
        const stillInStack = store.getState().active.stackOverlayIds.includes(id.current)

        // If after we
        if (stillInStack) {
          setActiveState({ stackOverlayIds: currentIds.filter(overlayId => overlayId !== id.current) })
        }
      }
    }, [])

    // Using the built in onClick means that if you start the click in the modal and end it in the dimmer area it closes
    // the modal. This is frustrating when selecting text in an input field. To avoid this we track if the click started
    // in the dimmer area and only close the modal if it did and it also ended in the dimmer area.
    const handleWeirdCloseMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      if (noWeirdClose) return
      if (e.clientX >= e.currentTarget.clientWidth) return // Skip if they clicked on a scrollbar
      if (e.target === e.currentTarget) setCloseClickStarted(true)
      else setCloseClickStarted(false)
    }
    const handleWeirdCloseMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
      if (noWeirdClose) return
      if (e.target === e.currentTarget && closeClickStarted) onClose && onClose()
      setCloseClickStarted(false)
    }

    const handleSetOffset = (desiredOffset: number) => {
      if (!swipeRef.current) return

      swipeRef.current.style.animation = 'unset'
      swipeRef.current.style.transform = 'hidden'
      swipeRef.current.style.transform = `translateX(${desiredOffset < 0 ? 0 : desiredOffset}px)`

      if (desiredOffset === 0)
        setTimeout(() => {
          if (!swipeRef.current) return

          // Cleanup after yo-self
          swipeRef.current.style.animation = ''
          swipeRef.current.style.transform = ''
          swipeRef.current.style.transform = ''
        }, 100)
    }

    const swipeRef = useSwipeEffects('horizontal', {
      swippingClassName: styles.activelyDragging,
      checkCanStartSwipe: () => !disableSwipeToClose && isActiveElement,
      onTouchMove: offset => handleSetOffset(offset),
      onTouchEnd: (offset, _position, speed) => {
        const swipeThresholdMet = offset > document.documentElement.clientWidth / 2 || (speed > 0.5 && offset > 0)

        if (!swipeThresholdMet) return handleSetOffset(0)

        setSkipTransition(true)
        handleSetOffset(document.body.clientWidth + 1)

        setTimeout(() => {
          setNoScroll(false)
          setActiveState({ stackOverlayIds: stackOverlayIds.filter(overlayId => overlayId !== id.current) })

          if (swipeRef.current) swipeRef.current.removeAttribute('style')

          onClose()
        }, 100)
      },
    })

    if (containerState === 'hidden' && contentState === 'hidden' && !remainMounted) return null

    // Used when dragging cards from search
    if (hideStackOverlay && containerTransitionRef.current) {
      containerTransitionRef.current.style.pointerEvents = 'none'
      containerTransitionRef.current.style.opacity = '0'
      containerTransitionRef.current.style.transition = 'opacity 200ms linear'
    }

    if (!hideStackOverlay && containerTransitionRef.current) {
      containerTransitionRef.current.style.pointerEvents = ''
      containerTransitionRef.current.style.opacity = ''
      containerTransitionRef.current.style.transition = ''
    }

    return ReactDOM.createPortal(
      <div
        className={`${styles.overlay} ${rootClassName}`}
        id={id.current}
        ref={el => {
          swipeRef.current = el
          containerTransitionRef.current = el
        }}>
        {isRootComponent && (
          <div className={styles.rootOverlay}>
            <div className={styles.grid} />
          </div>
        )}

        <div
          ref={el => {
            if (parentRef) parentRef.current = el

            setContainerRef(el)
            contentRef.current = el
          }}
          className={styles.content}
          onMouseUp={handleWeirdCloseMouseUp}
          onMouseDown={handleWeirdCloseMouseDown}>
          <div className={`${styles.primaryContent} ${className}`}>{children}</div>

          <div className={styles.desktopNavigation}>
            <button onClick={onClose}>
              <Icon name={isRootComponent ? 'close' : 'arrow left'} />
              <span>{isRootComponent ? 'Close' : 'Back'}</span>
            </button>
          </div>

          <FloatingToolbar
            className={styles.mobileToolbar}
            hideOnScrollRef={containerRef}
            options={[
              {
                icon: isRootComponent ? 'close' : 'arrow left',
                label: isRootComponent ? 'Close' : 'Back',
                onClick: onClose,
              },
              ...mobileToolbarOptions,
            ]}
          />
        </div>
      </div>,
      document.body,
    )
  },
)

export default GlobalOverlayStack
